import { css } from '@emotion/react'
import { MouseEvent, useEffect, useState } from 'react'
import { fetchActivities, fetchArticleById, fetchSimplePostById } from 'utils/httpApi'
import { ActivityRecord, Photo, PhotoWithIndex } from 'type'
import { TERTIARY_CONTAINER_COLOR } from 'style'
import { PHOTO_DISPLAY_PATH } from 'config'
import { useDisplayedPhoto } from 'globalState/useDisplayedPhoto'
import { useNavigate } from 'react-router-dom'
import { TextButton, MediaGrid } from 'component'

export const Activity = () => {
    const [activities, setActivities] = useState<ActivityRecord[]>([])
    const [loading, setLoading] = useState(true)
    const [selectedPhotos, setSelectedPhotos] = useState<PhotoWithIndex[]>([])
    const [, setDisplayedPhoto] = useDisplayedPhoto()
    const navigate = useNavigate()

    useEffect(() => {
        fetchActivities().then(res => {
            setActivities(res)
            setLoading(false)
        }).catch(e => {
            console.error(e)
            setLoading(false)
        })
    }, [])

    const formatDate = (timestamp: number) => {
        const date = new Date(timestamp)
        return `${date.getMonth() + 1}月${date.getDate()}日`
    }

    const onActivityClick = async (activity: ActivityRecord) => {
        setLoading(true)
        try {
            let photos: Photo[] = []
            if (activity.activityType === 'ARTICLE') {
                const res = await fetchArticleById(activity.childId)
                photos = res.photos
            } else if (activity.activityType === 'SIMPLE_POST') {
                const res = await fetchSimplePostById(activity.childId)
                photos = res.photos
            }

            const photosWithIndex = photos.map((v, idx): PhotoWithIndex => ({
                ...v,
                index: idx
            }))
            setSelectedPhotos(photosWithIndex)
        } catch (e) {
            console.error(e)
        } finally {
            setLoading(false)
        }
    }

    const onPhotoClick = (event: MouseEvent<HTMLImageElement>, index: number) => {
        setDisplayedPhoto(selectedPhotos[index])
        navigate(PHOTO_DISPLAY_PATH, { state: { isSwipe: true, photos: selectedPhotos } })
    }

    const onVideoClick = (event: MouseEvent<HTMLDivElement>, index: number) => {
        setDisplayedPhoto(selectedPhotos[index])
        navigate(PHOTO_DISPLAY_PATH, { state: { isSwipe: true, photos: selectedPhotos } })
    }

    if (loading) {
        return <div css={messageCss}>読み込み中...</div>
    }

    if (selectedPhotos.length > 0) {
        return (
            <div css={containerCss}>
                <div css={backButtonContainerCss}>
                    <TextButton onClick={() => setSelectedPhotos([])}>戻る</TextButton>
                </div>
                <MediaGrid photos={selectedPhotos} onPhotoClick={onPhotoClick} onVideoClick={onVideoClick} />
            </div>
        )
    }

    if (activities.length === 0) {
        return <div css={messageCss}>アクティビティがありません</div>
    }

    return (
        <div css={containerCss}>
            <ul css={listCss}>
                {activities.map(activity => (
                    <li key={activity.id} css={itemCss} onClick={() => onActivityClick(activity)}>
                        <span css={dateCss}>{formatDate(activity.createdAt)}</span>
                        <span css={textCss}>に投稿がありました</span>
                    </li>
                ))}
            </ul>
        </div>
    )
}

const containerCss = css`
    background-color: white;
    height: 100%;
`

const listCss = css`
    list-style: none;
    padding: 0;
    margin: 0;
`

const itemCss = css`
    padding: 12px;
    border-bottom: 1px solid ${TERTIARY_CONTAINER_COLOR};
    display: flex;
    align-items: center;
    cursor: pointer;
    &:hover {
        background-color: #f9f9f9;
    }
`

const dateCss = css`
    font-weight: bold;
    margin-right: 4px;
`

const textCss = css`
    color: #333;
`

const messageCss = css`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    color: #666;
`

const backButtonContainerCss = css`
    margin-bottom: 16px;
`